import { Tabs as BaseTabs } from '@base-ui-components/react/tabs'
import React, { useCallback, useEffect, useRef, useState, type FC, type HTMLAttributes, type ReactNode } from 'react'
import styles from './tabs.module.css'

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string | number
  onChange?: (event: React.SyntheticEvent | null, value: string | number) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth'
  orientation?: 'horizontal' | 'vertical'
  /** @deprecated Ignored - kept for MUI compat */
  scrollButtons?: boolean | 'auto'
}

const SCROLL_AMOUNT = 200

export const Tabs: FC<TabsProps> = ({
  value,
  onChange,
  variant: _variant,
  orientation = 'horizontal',
  scrollButtons: _scrollButtons,
  className,
  children,
  ...rest
}) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [canScrollStart, setCanScrollStart] = useState(false)
  const [canScrollEnd, setCanScrollEnd] = useState(false)
  const isVertical = orientation === 'vertical'
  const hasOverflow = canScrollStart || canScrollEnd

  const updateScrollState = useCallback(() => {
    const el = listRef.current
    if (!el) {
      return
    }
    if (isVertical) {
      setCanScrollStart(el.scrollTop > 0)
      setCanScrollEnd(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
    } else {
      setCanScrollStart(el.scrollLeft > 0)
      setCanScrollEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }
  }, [isVertical])

  useEffect(() => {
    const el = listRef.current
    if (!el) {
      return
    }
    updateScrollState()
    const obs = new ResizeObserver(updateScrollState)
    obs.observe(el)
    el.addEventListener('scroll', updateScrollState, { passive: true })
    return () => {
      obs.disconnect()
      el.removeEventListener('scroll', updateScrollState)
    }
  }, [updateScrollState])

  const scroll = (direction: -1 | 1) => {
    const el = listRef.current
    if (!el) {
      return
    }
    const prop = isVertical ? 'top' : 'left'
    el.scrollBy({ [prop]: direction * SCROLL_AMOUNT, behavior: 'smooth' })
  }

  const classes = [styles.tabs, isVertical ? styles.vertical : undefined, className].filter(Boolean).join(' ')

  // Auto-assign index-based values to Tab children without explicit values (MUI compat)
  let tabIndex = 0
  const indexedChildren = React.Children.map(children, child => {
    if (React.isValidElement<TabProps>(child) && child.type === Tab) {
      const idx = tabIndex++
      if (child.props.value == null) {
        return React.cloneElement(child, { value: idx })
      }
    }
    return child
  })

  return (
    <BaseTabs.Root
      value={value ?? null}
      onValueChange={val => {
        if (val != null) {
          onChange?.(null, val as string | number)
        }
      }}
    >
      <div className={[styles.container, isVertical ? styles.containerVertical : undefined].filter(Boolean).join(' ')}>
        {hasOverflow ? (
          <button
            type="button"
            className={[styles.scrollBtn, canScrollStart ? undefined : styles.scrollBtnHidden].filter(Boolean).join(' ')}
            aria-label={isVertical ? 'Scroll up' : 'Scroll left'}
            aria-hidden={!canScrollStart}
            tabIndex={canScrollStart ? 0 : -1}
            onClick={() => scroll(-1)}
          >
            {isVertical ? '\u25B2' : '\u25C0'}
          </button>
        ) : null}
        <BaseTabs.List ref={listRef} className={classes} {...rest}>
          {indexedChildren}
        </BaseTabs.List>
        {hasOverflow ? (
          <button
            type="button"
            className={[styles.scrollBtn, canScrollEnd ? undefined : styles.scrollBtnHidden].filter(Boolean).join(' ')}
            aria-label={isVertical ? 'Scroll down' : 'Scroll right'}
            aria-hidden={!canScrollEnd}
            tabIndex={canScrollEnd ? 0 : -1}
            onClick={() => scroll(1)}
          >
            {isVertical ? '\u25BC' : '\u25B6'}
          </button>
        ) : null}
      </div>
    </BaseTabs.Root>
  )
}

export interface TabProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'value'> {
  label?: ReactNode
  value?: string | number
  icon?: ReactNode
  disabled?: boolean
  iconPosition?: 'start' | 'end' | 'top' | 'bottom'
}

export const Tab: FC<TabProps> = ({ label, icon, disabled, value, className, children, ...rest }) => {
  const classes = [styles.tab, className].filter(Boolean).join(' ')

  return (
    <BaseTabs.Tab className={classes} value={value} disabled={disabled} {...rest}>
      {icon}
      {label ?? children}
    </BaseTabs.Tab>
  )
}
